import { FunctionComponent, useCallback, useEffect, useState, useRef, useLayoutEffect, useMemo } from 'react'
import styled from 'styled-components'
import Logo from './Logo'
import { styles } from './../styles/themes/style';
import ProfilePhotoDropdown from './dropdown/header/ProfilePhotoDropdown';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppRoutes, TMenuItemState } from '../interfaces/store/appStore';
import { useAppStore } from '../stores/application/useAppStore';
import { useAppHandlers } from '../services/actions/app/useAppHandlers';
import { useAppActions } from '../stores/application/useAppActions';
import { useSwipeable } from "react-swipeable";
import useMediaQuery from "./useMediaQuery";
import SvgFragment from "../fragments/SvgFragment";
import { Plus, RefreshCw } from 'lucide-react';
import Row from './Row';
import { useTasksHandlers } from '../services/actions/tasks/useTasksHandlers';
import { useAuthStore } from '../stores/auth/useAuthStore';
import Notifications from '../fragments/Notifications';
import TooltipWrapper from './TooltipWrapper';
import moment from 'moment';
import { useAuthHandlers } from '../services/actions/auth/useAuthHandlers';
import Column from './Column';
import DropdownMenu from './dropdown/DropdownMenu';
import DropdownItem from './dropdown/DropdownItem';
import Text from './Text';
import { isInboxNavDisabledForStarter, shouldOpenNewInbox } from '../services/inbox/inboxNavAccess';
import { triggerManualLinkedInSync } from '../pages/onboarding/linkedinConnectFlowUtils';
import { getInboxAccessBlockedTooltip, getSyncContactsTooltip, SYNC_CONTACTS_EXTENSION_REQUIRED_TOOLTIP } from '../utils/syncContactsTooltips'
import { isChromeBasedBrowser, isMobileDevice } from '../utils';

interface IHeaderProps { }

const isAppInboxNavItem = (item: TMenuItemState) => item.routeInternal === AppRoutes.INBOX
const isExtensionInboxNavItem = (item: TMenuItemState) => item.extension === 'messaging'
const isUnifiedInboxNavItem = (item: TMenuItemState) => !!item.inboxNav
const isInboxNavItem = (item: TMenuItemState) =>
  isUnifiedInboxNavItem(item) || isAppInboxNavItem(item) || isExtensionInboxNavItem(item)

const getNavBadgeLabel = (item: TMenuItemState) => item.badge ?? (item.isNew ? 'NEW' : null)

const NavPanelToggle = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    margin-right: 4px;
    padding: 0;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: ${styles.colors.black300};
    cursor: pointer;

    &:hover {
        background-color: #f3f6fb;
    }

    .svg {
        width: 20px;
        height: 20px;
    }
`;

const Header: FunctionComponent<IHeaderProps> = () => {
  const navigate = useNavigate();

  const {
    store: { headerState, extensionInstalled, extensionVersion, onboardingStep, mobileNavOpen = false },
  } = useAppStore(); //todo: set this component somewhere where it makes more sense
  const [headerList, setHeaderList] = useState<TMenuItemState[]>([]);
  const { openExtension, setSyncConnectionsModalHandler, setAddMembersModal } = useAppHandlers();
  const { setMobileNavOpen } = useAppActions();
  const isMobile = isMobileDevice();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { completeSingleTask } = useTasksHandlers()
  const { store: { inboxTaskId, user, authData, disablePremiumFeatures, isLoggedToLinkedin, workspace, trial, subscription, lifetime } } = useAuthStore()
  const { syncConnectionsHandler, syncConversationsHandler, getUserHandler, canSyncConnectionsNow, getSyncLimitInfo } = useAuthHandlers()
  const isSmallMobile = useMediaQuery("(max-width: 968px)");

  /** Starter: Inbox locked until inboxEnabled (admin). */
  const isStarterLockedNavItem = useCallback(
    (item: TMenuItemState) =>
      (isInboxNavItem(item) && isInboxNavDisabledForStarter(workspace)) ||
      item.title === 'Templates' ||
      item.routeInternal === AppRoutes.INTEGRATIONS,
    [workspace]
  )

  /* const disableInbox = //todo: check this
        localStorage.getItem('newInboxUser') !== null ?
            localStorage.getItem('newInboxUser') === 'true' ? true : false
        : false */
  const location = useLocation()
  const pathname = location.pathname

  useEffect(() => {
    let headerList: any = [];
    if (headerState)
      headerList = Object.keys(headerState).map((key) => ({
        ...headerState[key],
      }));

    setHeaderList(headerList);
  }, [headerState]);

  const handleClick = useCallback(
    async (headerItem: TMenuItemState) => {
      if (isInboxNavItem(headerItem) && inboxTaskId) await completeSingleTask(inboxTaskId, true, false)

      if (isUnifiedInboxNavItem(headerItem)) {
        if (shouldOpenNewInbox(workspace, lifetime)) {
          navigate(AppRoutes.INBOX)
        } else {
          openExtension({ query: 'redirectTo=inbox' })
        }
        return
      }
      
      // Special handling for Custom feeds: if on /posts and showing Manage feeds or Single feed view,
      // clear localStorage to return to main Feed page
      if (headerItem.title === 'Custom feeds' && pathname === AppRoutes.POSTS) {
        const showManageFeeds = localStorage.getItem('postsSection_showManageFeeds') === 'true';
        const showConnections = localStorage.getItem('postsSection_showConnections') === 'true';
        
        if (showManageFeeds || showConnections) {
          // Clear localStorage to return to main Feed page
          localStorage.removeItem('postsSection_showManageFeeds');
          localStorage.removeItem('postsSection_showConnections');
          localStorage.removeItem('postsSection_selectedFeedId');
          // Force page reload to ensure state is reset
          window.location.href = AppRoutes.POSTS;
          return;
        }
      }
      
      if (headerItem.routeInternal) navigate(headerItem.routeInternal ?? "");
      if (headerItem.extension) openExtension({ query: `redirectTo=${headerItem.extension}`})
    },
    [inboxTaskId, pathname, navigate, openExtension, completeSingleTask, lifetime, workspace]
  );

  //todo: encapsulate and add tooltips and create burger menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => setIsMenuOpen(false),
    onSwipedRight: () => setIsMenuOpen(true),
  });

  const getTooltipText = (headerItem: any, isLoggedToLinkedin: boolean, extensionInstalled: boolean, extensionVersion: string) => {
    if(isInboxNavItem(headerItem)){
      return getInboxAccessBlockedTooltip({
        isStarterLocked: isInboxNavDisabledForStarter(workspace),
        extensionInstalled,
        extensionVersion,
        isLoggedToLinkedin,
        isSynced: user?.isSynced,
      })
    } else if(workspace.isStarter && (headerItem?.title === 'Templates' || headerItem?.routeInternal === AppRoutes.INTEGRATIONS)){
      return "Upgrade to Pro to unlock this feature";
    } else if((headerItem?.routeInternal === AppRoutes.PROSPECTS) && (!workspace.isBusiness && !workspace.prospectsEnabled)){
      return "Upgrade to Business to unlock this feature";
    } else if (
      headerItem?.title === 'AI Chat' &&
      !(
        workspace?.aiChatEnabled ||
        trial ||
        (workspace?.isBusiness && subscription?.status === 'active')
      )
    ) {
      return "Upgrade to Business to unlock this feature";
    } else if(headerItem.routeInternal === AppRoutes.CSV_ENRICHMENT && trial){
      return "CSV enrichment available only on paid plans";
    } else {
      return '';
    }
  }

  const isDisabled = (headerItem: any, isLoggedToLinkedin: boolean, extensionInstalled: boolean, extensionVersion: string) => {
    return (
      (isInboxNavItem(headerItem) && (
        !isChromeBasedBrowser() ||
        !isLoggedToLinkedin ||
        !extensionInstalled ||
        !user.isSynced ||
        extensionVersion !== process.env.REACT_APP_VERSION
      )) ||
      (
        workspace.isStarter &&
        isStarterLockedNavItem(headerItem)
      ) ||
      (
        (headerItem?.routeInternal === AppRoutes.PROSPECTS) && (!workspace.isBusiness && !workspace.prospectsEnabled)
      ) ||
      (
        headerItem?.title === 'AI Chat' &&
        !(
          workspace?.aiChatEnabled ||
          trial ||
          (workspace?.isBusiness && subscription?.status === 'active')
        )
      )
      ||
      (
        headerItem.routeInternal === AppRoutes.CSV_ENRICHMENT && trial
      )
    )
  }

  const setLastSyncAt = useCallback(() => {
    if (!user) return

    if (user.isSynced) {
      syncConnectionsHandler(user)
      return
    }

    if (!extensionInstalled) return

    const runSync = () => {
      syncConnectionsHandler(user, false, authData)
      syncConversationsHandler()
      void getUserHandler()
    }

    triggerManualLinkedInSync({
      user,
      extensionInstalled,
      onboardingStep,
      onSync: runSync,
      onLegacySyncModal: () => setSyncConnectionsModalHandler(() => runSync()),
    })
  }, [
    user,
    extensionInstalled,
    authData,
    onboardingStep,
    getUserHandler,
    setSyncConnectionsModalHandler,
    syncConnectionsHandler,
    syncConversationsHandler,
  ])

  const lastSyncAtLocalStorage = window.localStorage.getItem('lastSyncAt')
  const lastSyncAt = lastSyncAtLocalStorage && lastSyncAtLocalStorage !== 'undefined' ? lastSyncAtLocalStorage : 0

  const isSyncDisabled =
    !isChromeBasedBrowser() ||
    (!user?.isSynced && !extensionInstalled) ||
    moment().isBefore(moment(lastSyncAt).add(15, 'minutes')) ||
    !canSyncConnectionsNow()

  const syncTooltipText = (() => {
    if (!isChromeBasedBrowser()) {
      return getSyncContactsTooltip(false)
    }
    if (!user?.isSynced && !extensionInstalled) {
      return SYNC_CONTACTS_EXTENSION_REQUIRED_TOOLTIP
    }
    const limit = getSyncLimitInfo()
    if (!canSyncConnectionsNow()) {
      return limit.nextAllowedAt
        ? `Sync limit reached. Next allowed: ${moment(limit.nextAllowedAt).fromNow()}`
        : 'Sync limit reached.'
    }
    return 'Sync your contacts'
  })()

  const navRef = useRef<HTMLDivElement>(null);
  const [visibleItemsCount, setVisibleItemsCount] = useState(100);
  const moreButtonWidth = 90;

  const filteredHeaderList = useMemo(() => {
      if (disablePremiumFeatures || pathname === AppRoutes.DELETE_USER_CONFIRMATION) return [];
      return headerList.filter((headerItem) => headerItem.visible);
  }, [headerList, disablePremiumFeatures, pathname]);

  useLayoutEffect(() => {
    if (isSmallMobile || !navRef.current) return;

    const calculateVisibleItems = (navWidth: number) => {
        let currentWidth = 0;
        let count = 0;
        const ctx = document.createElement('canvas').getContext('2d');
        if(!ctx) return;
        ctx.font = "bold 14px NunitoSans";

        for (let i = 0; i < filteredHeaderList.length; i++) {
            const item = filteredHeaderList[i];
            const textWidth = ctx.measureText(item.title).width;
            let extraWidth = 0;
            // Check for lock
            const isLock = (workspace.isStarter && isStarterLockedNavItem(item)) || ((!workspace.isBusiness) && (item.routeInternal === AppRoutes.PROSPECTS) && !workspace.prospectsEnabled);
            if (isLock) extraWidth += 24; // Icon + margin
            if (getNavBadgeLabel(item)) extraWidth += 30; // Tag width

            const totalItemWidth = textWidth + 24 + extraWidth; // 24 is padding 12+12

            if (currentWidth + totalItemWidth + (i < filteredHeaderList.length - 1 ? moreButtonWidth : 0) <= navWidth) {
                currentWidth += totalItemWidth;
                count++;
            } else {
                 if (i === filteredHeaderList.length - 1 && currentWidth + totalItemWidth <= navWidth) {
                    count++;
                 }
                 break;
            }
        }
        setVisibleItemsCount(count);
    };

    const observer = new ResizeObserver(entries => {
        for (let entry of entries) {
             calculateVisibleItems(entry.contentRect.width);
        }
    });
    
    if (navRef.current) {
        observer.observe(navRef.current);
    }
    return () => observer.disconnect();
  }, [filteredHeaderList, isSmallMobile, workspace, moreButtonWidth, isStarterLockedNavItem]);

  const visibleList = isSmallMobile ? filteredHeaderList : filteredHeaderList.slice(0, visibleItemsCount);
  const overflowList = isSmallMobile ? [] : filteredHeaderList.slice(visibleItemsCount);
  // Temporary switch: hide page navigation buttons in header.
  const showPageNavButtons = false;

  const renderNavItem = (headerItem: TMenuItemState, index: number) => {
      if (isExtensionInboxNavItem(headerItem) && isSmallMobile) return null;

      const isCustomFeedSelected = headerItem.title === 'Custom feeds' && headerItem.routeInternal === pathname;
      const isInSubView = isCustomFeedSelected && (
        localStorage.getItem('postsSection_showManageFeeds') === 'true' ||
        localStorage.getItem('postsSection_showConnections') === 'true'
      );
      const shouldAllowClick = isCustomFeedSelected && isInSubView;
      const isSelected = headerItem.routeInternal === pathname && !shouldAllowClick;

      return (
        <TooltipWrapper key={index} tooltipText={getTooltipText(headerItem, isLoggedToLinkedin, extensionInstalled, extensionVersion)}>
          <NavButton
            $disabled={isDisabled(headerItem, isLoggedToLinkedin, extensionInstalled, extensionVersion)}
            $selected={isSelected}
            $allowClick={shouldAllowClick}
          >
            <a 
              href={headerItem.routeInternal || '#'}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isDisabled(headerItem, isLoggedToLinkedin, extensionInstalled, extensionVersion)) {
                  handleClick(headerItem);
                }
              }}
              style={{ 
                cursor: shouldAllowClick ? 'pointer' : undefined,
                pointerEvents: shouldAllowClick ? 'auto' : undefined
              }}
            >
              {headerItem.title}
            </a>
            {getNavBadgeLabel(headerItem) && (
              <NewTag justifyCenter alignItems>
                {getNavBadgeLabel(headerItem)}
              </NewTag>
            )}
            {(workspace.isStarter && isStarterLockedNavItem(headerItem)) || ((!workspace.isBusiness) && (headerItem?.routeInternal === AppRoutes.PROSPECTS)  && !workspace.prospectsEnabled) ?
              <LockContainer>
                  <SvgFragment type={"lock2"} />
              </LockContainer> : null}
          </NavButton>
        </TooltipWrapper>
      );
  };

  return (
    <Container {...swipeHandlers}>
      <LogoContainer>
      {isMobile && user && !mobileNavOpen && (
        <NavPanelToggle
          type="button"
          aria-label="Open navigation"
          onClick={(e) => {
            e.stopPropagation();
            setMobileNavOpen(true);
          }}
        >
          <SvgFragment type="panelLeftOpen" />
        </NavPanelToggle>
      )}

      {!isSmallMobile && (<>
          {showPageNavButtons && (
            <Nav isOpen={isMenuOpen} ref={navRef}>
              {visibleList.map((headerItem, index) => renderNavItem(headerItem, index))}

              {overflowList.length > 0 && (
                    <MoreDropdownMenu
                      title="More"
                      tooltipMessage=""
                      drop="down"
                  >
                      {overflowList.map((headerItem, index) => (
                          <DropdownItem
                              key={index}
                              onClickHandler={() => isDisabled(headerItem, isLoggedToLinkedin, extensionInstalled, extensionVersion) ? undefined : handleClick(headerItem)}
                              $disabled={isDisabled(headerItem, isLoggedToLinkedin, extensionInstalled, extensionVersion)}
                          >
                            <Row alignItems>
                              <Text $bold>{headerItem.title}</Text>
                              {getNavBadgeLabel(headerItem) && (
                                <NewTag justifyCenter alignItems>
                                  {getNavBadgeLabel(headerItem)}
                                </NewTag>
                              )}
                              {(workspace.isStarter && isStarterLockedNavItem(headerItem)) || ((!workspace.isBusiness) && (headerItem?.routeInternal === AppRoutes.PROSPECTS) && !workspace.prospectsEnabled) ?
                                  <LockContainer>
                                      <SvgFragment type={"lock2"} />
                                  </LockContainer> : null}
                              </Row>
                          </DropdownItem>
                      ))}
                  </MoreDropdownMenu>
              )}
            </Nav>
          )}
        </>
      )}
      </LogoContainer>
      {user && (
        <HeaderActions>
          {onboardingStep < 0 && pathname !== AppRoutes.DELETE_USER_CONFIRMATION && !disablePremiumFeatures && (
            <>
              <TooltipWrapper tooltipText={workspace?.isStarter ? 'Upgrade to Pro or Business to unlock this feature' : ''}>
                <AddMembersButton
                  type="button"
                  disabled={workspace?.isStarter}
                  onClick={() => setAddMembersModal()}
                >
                  <Plus size={16} strokeWidth={2} />
                  Add members
                </AddMembersButton>
              </TooltipWrapper>
              <TooltipWrapper tooltipText={syncTooltipText}>
                <HeaderIconButton
                  type="button"
                  aria-disabled={isSyncDisabled}
                  onClick={() => {
                    if (isSyncDisabled) return
                    setLastSyncAt()
                  }}
                >
                  <RefreshCw size={16} strokeWidth={2} />
                </HeaderIconButton>
              </TooltipWrapper>
              <Notifications />
            </>
          )}
          <ProfilePhotoDropdown />
        </HeaderActions>
      )}
    </Container>
  );
};

const Container = styled.div`
    height: ${styles.lengths.navbarHeight};
    width: 100%;
    padding: 0px 16px 0px 0px;
    border-bottom: 2px solid ${({ theme: { quinaryColor } }) => quinaryColor};
    background-color: ${({ theme: { secondaryColor } }) => secondaryColor};
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const LogoContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    flex: 1;
    min-width: 0;
`;


const SlideMenu = styled.div<{ isOpen: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 318px;
    background-color: ${({ theme: { secondaryColor } }) => secondaryColor};
    box-shadow: 2px 0 5px rgba(0, 0, 0, 0.5);
    transform: ${({ isOpen }) =>
      isOpen ? "translateX(0)" : "translateX(-100%)"};
    transition: transform 0.3s ease;
    z-index: 1006;
    padding: 16px;
`;

const NavButton = styled.div<{$disabled: boolean, $selected: boolean, $allowClick?: boolean}>`
    position: relative;
    line-height: 20px;
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: bold;
    padding: 0px 12px;
    height: 100%;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    a {
      ${({ theme: { primaryColor } }) => primaryColor && `color: ${primaryColor};`}
      white-space: nowrap;
    }
    ${({ $disabled }) => $disabled && `
        cursor: default;
        a, &:hover a, &:focus a{
            cursor: default;
            color: ${styles.colors.disabledState} !important;
        }
    `}
    ${({ $selected, $allowClick }) => $selected && !$allowClick && `
        border-bottom: 2px solid #5964FF;
        cursor: default;
        a {
            cursor: default;
            color: ${styles.colors.primary600} !important;
        }
    `}
    ${({ $allowClick }) => $allowClick && `
        cursor: pointer !important;
        border-bottom: 2px solid #5964FF;
        a {
            cursor: pointer !important;
            color: ${styles.colors.primary600} !important;
        }
        &:hover a {
            color: ${styles.colors.primary500} !important;
        }
    `}
`

const Nav = styled.nav<{ isOpen: boolean }>`
    display: flex;
    height: ${styles.lengths.navbarHeight};
    width: 100%;
    padding: 0 4px;
    align-items: center;
    justify-content: flex-start;
    overflow: visible;
    @media (max-width: 968px) {
      display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
      flex-direction: column;
      width: 100%;
      align-items: start;
      padding: 0;
      overflow: visible;

      > * {
        margin: 8px 0;
      }
    }
`;

const LogoEntireContainer = styled.span`
    display: flex;
    width: 100%;
    height: 24px;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 16px;
`;

const CloseSidebar = styled.div`
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    .svg {
      width: 24px;
      height: 24px;
      .path {
        fill: ${({ theme: { disabledState } }) => disabledState};
      }
    }
`;

const LockContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-left: 8px;
`;

const HeaderActions = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
`

const AddMembersButton = styled.button`
    display: none;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 16px;
    border-radius: 9999px;
    background: ${({ theme: { secondaryColor } }) => secondaryColor};
    border: 1px solid ${({ theme: { quinaryColor } }) => quinaryColor};
    color: ${styles.colors.black600};
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    flex-shrink: 0;

    @media (min-width: 768px) {
        display: flex;
    }

    &:hover:not(:disabled) {
        background: ${styles.colors.black100};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    svg {
        flex-shrink: 0;
    }
`

const HeaderIconButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid transparent;
    background: transparent;
    color: ${styles.colors.black300};
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
    flex-shrink: 0;

    &:hover:not([aria-disabled='true']) {
        background: ${styles.colors.black100};
        color: ${styles.colors.black400};
    }

    &[aria-disabled='true'] {
        color: ${({ theme: { primary } }) => primary ? styles.colors.black200 : styles.colors.black300};
        cursor: default;
        opacity: 0.7;
    }
`
const NewTag = styled(Column)`
    position: relative;
    right: -3px;
    height: 12px;
    width: 25px;
    font-size: 8px;
    background: ${styles.colors.primary600};;
    border-radius: 3px;
    color: ${styles.colors.white};
`

const MoreDropdownMenu = styled(DropdownMenu)`
    & .dropdown-toggle:hover {
        opacity: 0.54;
    }

    .dropdown-toggle, .dropdownWrapperReference {
        color: ${styles.colors.black600} !important;
        padding: 0px 6px;
    }
`

export default Header;
